import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import * as fromRoot from '../../reducers';
import { ErrCode, User } from "src/app/shared/models";
import { ResetPasswordService } from "src/app/core/resetpassword.service";
import { LoggerService } from "src/app/core/logger.service";
import { ActivatedRoute, Router } from "@angular/router";
import { passwordStrength } from 'check-password-strength'
import { PasswordService } from "../services/password.service";
import { BlendService } from "src/app/shared/services/blend.service";
import { SyncCookieService } from "src/app/core/sync-cookie.service";
import { UserService } from "src/app/core/user.service";

interface Form {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

type Fields = keyof Form;

@Component({
  templateUrl: './invite-password.component.html',
  styleUrls: ['./invite-password.component.scss']
})
export class InvitePasswordComponent implements OnInit {
  protected form: FormGroup<Form>;
  private authenticated: boolean;
  protected user: User;
  private confirmCode: string;
  protected errcode: ErrCode;
  protected passErrcode: ErrCode;
  protected spinner = true;
  protected isInviteChecked = false;
  private submitted = false;
  protected email: string;
  public year: number = new Date().getFullYear();

  constructor(
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private rsService: ResetPasswordService,
    private log: LoggerService,
    private router: Router,
    private route: ActivatedRoute,
    private passwordService: PasswordService,
    private blendService: BlendService,
    private syncCookieService: SyncCookieService,
    private userService: UserService,
  ) { }

  ngOnInit() {
    this.form = this.fb.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    });

    this.form.controls.confirmPassword.valueChanges.subscribe(() => {
      this.submitted = false;
    });

    this.store
      .select(fromRoot.getAuthState)
      .subscribe((data) => {
        this.authenticated = data.authenticated;
        if (!data.authenticated) {
            this.user = new User();
            return;
        }
        if (data.user) {
            this.user = data.user;
        }
      });


    this.confirmCode = this.route.snapshot.queryParams['c'];
    if (this.confirmCode) {
      this.verifyEmailInvite();
    }
    else {
      this.errcode = new ErrCode(8103, 'No confirmation code provided');
    }
  }

  private async verifyEmailInvite() {
    try {
      const result = await this.rsService.verifyEmailInvite(this.confirmCode);
      if (result.error_code) {
        this.errcode = new ErrCode(result.error_code);
      } else {
        this.email = result.email;
        this.isInviteChecked = true;
      }
    } catch (ex) {
      this.log.error(ex);
      this.errcode = ErrCode.fromException(ex);
    }
    this.spinner = false;
  }

  protected async resetPasswordSubmit() {
    this.submitted = true;
    this.errcode = undefined;
    let pwd = this.form.controls.password.value;
    let cpwd = this.form.controls.confirmPassword.value;
    if (!this.passwordService.isPasswordStrong(pwd)) {
        this.passErrcode = new ErrCode(8304);
        pwd = undefined;
        cpwd = undefined;
        return;
    }
    if (pwd != cpwd) {
        this.passErrcode = new ErrCode(8121);
        pwd = undefined;
        cpwd = undefined;
        return;
    }

    this.spinner = true;

    try {
      const result = await this.rsService.resetPasswordSubmit(this.confirmCode, pwd);
      // Awaiting this prevents the connection from being severed due to reroute
      await this.blendService.trackSignUp(result['user_id'], {
        Signup_from: 'legacy_team_invite',
        Attribution_Mtags: this.syncCookieService.getMarketingRef(),
      });
      
      if (this.authenticated && this.user.id == result['user_id']) {
        const dashboard = this.userService.get('dashboard') || 'files';
        this.router.navigate([`/${dashboard}`], { queryParams: undefined });
      } else {
        this.router.navigate(['/login'], { queryParams : { passwordset: 1 } });
      }
    } catch (ex) {
      this.log.error(ex);
      this.errcode = ErrCode.fromException(ex);
    }
    this.spinner = false;
  }

  private isPasswordValid() {
    return this.checkPassword().length === 0;
  }

  protected get canSubmit() {
    return this.form.valid && this.isPasswordValid() && this.form.controls.password.value === this.form.controls.confirmPassword.value;
  }

  private checkPassword() {
    const errors = [];
    if (this.form.controls.password.value.length < 8 || this.form.controls.confirmPassword.value.length > 72) {
      errors.push('length');
    }
    return errors;
  }

  protected requirementPassed(name: 'length') {
    return this.checkPassword().indexOf(name) === -1;
  }

  protected getErrorMessage(name: Fields) {
    if (!this.form.controls[name].touched) return;

    if (this.form.controls[name].hasError('required')) {
      return 'This field is required';
    }
    else if (name == 'password') {
        if (!this.isPasswordValid()) {
          return 'Please enter a valid password';
        }
      }
      else if (name == 'confirmPassword') {
        if (this.form.controls.confirmPassword.value !== this.form.controls.password.value) {
          return 'Passwords do not match';
        }
      }
  }
}
